import {useEffect,useRef,useState} from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import IconShield from '@mui/icons-material/Shield';
import LockIcon from '@mui/icons-material/Lock';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import {getStates} from '../../helpers/selectOptions';
import {useCreateCustomerMutation} from '../../redux/services/paymentApi';
import CustomTextField from '../common/CustomTextField';
import CustomInputLabel from '../common/CustomInputLabel';
import TitleDivider from '../patientSignup/TitleDivider';

const STAX_LOAD_TIMEOUT_MS = 15000;
const TOKENIZE_TIMEOUT_MS = 20000;
const TOKENIZE_RETRY_DELAY_MS = 3000;
const GENERIC_PAYMENT_ERROR = 'There was a problem processing your payment method. Please check your card details and try again.';
const FORM_LOAD_ERROR = 'Unable to load the payment form. Please refresh the page or contact us at (202) 681-1474 or info@movement-x.com if this continues.';

const withTimeout = (promise, ms, timeoutMessage) => {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
};

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const inputStyle = {
  fontSize: '0.84375rem',
  fontFamily: 'Inter, Helvetica Neue, Helvetica',
  fontWeight: '300',
  color: '#31325f',
  height: '35px',
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  backgroundColor: 'white',
};

const useStax = ({setStaxJs, setPayButtonDisabled, setStaxFormLoading, setError, staxFormLoadingRef}) => {
  useEffect(() => {
    const StaxJs = window.StaxJs;
    if (!StaxJs) {
      console.error('StaxJs library not loaded');
      setError({message: FORM_LOAD_ERROR});
      setStaxFormLoading(false);
      return;
    }
    let staxJs;
    try {
      staxJs = new StaxJs(config().staxPublicToken, {
        number: {
          id: 'stax-card-number',
          placeholder: '0000 0000 0000 0000',
          style:
            'height: 35px; width: 100%; padding: 10px 12px; font-size: 0.84375rem; font-family: Inter, Helvetica Neue, Helvetica; color: #31325f; font-weight: 300; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box;' // 35px is the height of my card-number div
        },
        cvv: {
          id: 'stax-card-cvv',
          placeholder: '123',
          style:
            'height: 35px; width: 100%; padding: 10px 12px; font-size: 0.84375rem; font-family: Inter, Helvetica Neue, Helvetica; color: #31325f; font-weight: 300; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box;' // 35px is the height of my card-cvv div
        },
      });
    } catch (error) {
      console.error('Error initializing StaxJs:', error);
      setError({message: FORM_LOAD_ERROR});
      setStaxFormLoading(false);
      return;
    }
    staxJs.showCardForm()
      .then(handler => {
        setStaxFormLoading(false);
      })
      .catch(error => {
        console.error('Error showing Stax card form:', error);
        setError({message: FORM_LOAD_ERROR});
        setStaxFormLoading(false);
      });

    staxJs.on('card_form_complete', message => {
      // activate pay button
      setPayButtonDisabled(false);
    });

    staxJs.on('card_form_incomplete', message => {
      setPayButtonDisabled(true);
    });

    setStaxJs(staxJs);

    // if showCardForm() never actually resolves (the iframe itself failed to load),
    // let the user know. Once it resolves, the form is confirmed loaded, and a
    // disabled button after that just means the entered card data is incomplete/invalid,
    // which is normal and shouldn't show this message.
    const stuckFormTimeout = setTimeout(() => {
      if (staxFormLoadingRef.current) {
        setError({message: 'The payment form is taking longer than expected to load. Please check your connection or try refreshing the page.'});
      }
    }, STAX_LOAD_TIMEOUT_MS);
    return () => clearTimeout(stuckFormTimeout);
  }, []);
};

const attemptTokenize = async (staxJs, extraOptions) => {
  const staxResponse = await withTimeout(
    staxJs.tokenize(extraOptions),
    TOKENIZE_TIMEOUT_MS,
    'This is taking longer than expected. Please check your connection and try again.'
  );
  const newCustomerId = staxResponse?.customer?.id;
  if (!newCustomerId) {
    // Stax did not actually return a usable customer/card reference; do not proceed
    // as if payment succeeded.
    throw new Error(GENERIC_PAYMENT_ERROR);
  }
  return newCustomerId;
};

const handleSubmit = ({
  staxJs,
  post,
  setPayButtonDisabled,
  setCustomerId,
  afterSubmit,
  firstName,
  lastName,
  email,
  month,
  year,
  addressState,
  addressZip,
  promoCode = null,
  customerId = null,
  setError,
  setIsSubmitting,
  setIsRetrying,
}) => async e => {
  if (!inputIsValid({firstName, lastName, month, year, addressState, addressZip})) {
    setError({message: 'Please double check your name, expiration date, state, and zip code are filled in correctly.'});
    return;
  }
  setPayButtonDisabled(true);
  setIsSubmitting(true);
  console.log('firstName, lastName, month, year', firstName, lastName, month, year);
  let extraOptions = {
    validate: false,
    method: 'card',
    firstname: firstName,
    lastname: lastName,
    email,
    month, // two digits
    year,  // two digits
    address_state: addressState,
    address_zip: addressZip,
  };
  if (customerId) extraOptions.customer_id = customerId;
  setError(null);

  let newCustomerId;
  try {
    newCustomerId = await attemptTokenize(staxJs, extraOptions);
  } catch (firstError) {
    console.error('Error tokenizing payment method (attempt 1 of 2):', firstError);
    setIsRetrying(true);
    await wait(TOKENIZE_RETRY_DELAY_MS);
    try {
      newCustomerId = await attemptTokenize(staxJs, extraOptions);
    } catch (secondError) {
      console.error('Error tokenizing payment method (attempt 2 of 2):', secondError);
      setError({message: secondError?.message || GENERIC_PAYMENT_ERROR});
      setPayButtonDisabled(false);
      setIsSubmitting(false);
      setIsRetrying(false);
      return;
    }
    setIsRetrying(false);
  }

  setPayButtonDisabled(false);
  setIsSubmitting(false);
  const formData = {
    customer_id: newCustomerId,
    is_stax: true,
    promo_code: promoCode,
  };
  if (afterSubmit) afterSubmit(formData);
};

const inputIsValid = formValues => {
  console.log('formValues', formValues);
  const {firstName, lastName, month, year, addressState, addressZip} = formValues;
  if (!firstName || firstName.length < 1) return false;
  if (!lastName || lastName.length < 1) return false;
  if (!month || month.length !== 2) return false;
  if (!year || year.length !== 4) return false;
  if (!addressState || addressState.length !== 2) return false;
  if (!addressZip || addressZip.length < 5) return false;
  return true;
};

const StaxPaymentForm = props => {
    const {isEditing = false, showOnboardingFields = false} = props;
    const [staxJs, setStaxJs] = useState(null);
    const [staxFormLoading, setStaxFormLoading] = useState(true);
    const [payButtonDisabled, setPayButtonDisabled] = useState(true);
    const [customerId, setCustomerId] = useState(props?.user?.patient?.stax_customer_id || null);
    const [firstName, setFirstName] = useState(props?.user?.user?.first_name || '');
    const [lastName, setLastName] = useState(props?.user?.user?.last_name || '');
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');
    const [addressState, setAddressState] = useState(props?.user?.address_state || props?.user?.patient?.address_state || '');
    const [addressZip, setAddressZip] = useState(props?.user?.address_zip || props?.user?.patient?.address_zip || '');
    const [promoCode, setPromoCode] = useState('');
    const [promoIsCollapsed, setPromoIsCollapsed] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const staxFormLoadingRef = useRef(staxFormLoading);
    useEffect(() => {
      staxFormLoadingRef.current = staxFormLoading;
    }, [staxFormLoading]);
    useStax({setStaxJs, setPayButtonDisabled, setStaxFormLoading, setError, staxFormLoadingRef});
    console.log('StaxPaymentForm.props', props);
    const onSubmit = handleSubmit({
      staxJs, 
      setPayButtonDisabled,
      setCustomerId,
      afterSubmit: props?.onSubmit,
      firstName,
      lastName,
      addressState,
      addressZip,
      email: props?.user?.email || props?.auth?.user?.email || props?.auth?.email,
      month,
      year,
      promoCode,
      customerId,
      setError,
      setIsSubmitting,
      setIsRetrying,
    });
    return (
      <Box style={{maxWidth: '350px'}}>
        {showOnboardingFields && <TitleDivider 
          text='Enter Payment Information'
          Icon={LockIcon}
        />}
        {showOnboardingFields && 
          <Typography variant='large500Tight' style={{marginTop: '0.5rem', marginBottom: '0.25rem'}}>
            You will{' '}
            <span style={{textDecoration: 'underline', fontWeight: 'bold'}}>not</span>{' '}
            be charged until your first session is completed. Payments are secure and encrypted.
          </Typography>
        }
        {error && 
          <Alert severity='error'>
            {error.message}
          </Alert>
        }
        {staxFormLoading &&
          <Stack direction='row' spacing={1} justifyContent='center' alignItems='center' style={{minHeight: '20px'}} py={1}>
            <CircularProgress size={18}/>
            <Typography variant='body2' color='text.muted'>Loading payment form...</Typography>
          </Stack>
        }
        <Grid container spacing={0.25} style={{marginTop: '1rem'}}>
          <Grid item xs={12} md={6}>
            <Stack>
              <CustomInputLabel required={true}>
                First Name
              </CustomInputLabel>
              <input
                name='firstname'
                style={{...inputStyle}}
                className='field'
                placeholder='Jane'
                type="text"
                autoComplete="given-name"
                defaultValue={props?.user?.user?.first_name || ''}
                onChange={e => setFirstName(e?.target?.value)}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack>
              <CustomInputLabel required={true}>
                Last Name
              </CustomInputLabel>
              <input
                name='lastname'
                style={{...inputStyle}}
                className='field'
                placeholder='Doe'
                type="text"
                autoComplete="family-name"
                defaultValue={props?.user?.user?.last_name || ''}
                onChange={e => setLastName(e?.target?.value)}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack>
              <CustomInputLabel required={true}>
                Card Number
              </CustomInputLabel>
              <div id='stax-card-number' className='card-number' style={{height: '35px'}}/>
            </Stack>
          </Grid>
          <Grid item xs={16} md={4}>
            <Stack>
              <CustomInputLabel required={true}>
                CVV
              </CustomInputLabel>
              <div id='stax-card-cvv' className='card-cvv' style={{height: '35px'}}/>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} justifyContent='center'>
            <Stack direction='row' alignItems='center' style={{paddingTop: '0.4rem'}}>
              <Stack>
                <CustomInputLabel required={true} sx={{padding: '0px'}}>
                  Expiration
                </CustomInputLabel>
                <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                  <input
                    className='field'
                    style={{...inputStyle, width: '50px'}}
                    name='month'
                    maxLength='2'
                    placeholder='MM'
                    autoComplete='cc-exp'
                    type="text"
                    inputMode='numeric'
                    pattern="[0-9]*"
                    onChange={e => setMonth(e?.target?.value)}
                  />
                  <div style={{fontSize: '1rem'}}>/</div>
                  <input
                    className='field'
                    style={{...inputStyle, width: '50px'}}
                    name='year'
                    maxLength='2'
                    placeholder='YY'
                    autoComplete='cc-exp'
                    type="text"
                    inputMode='numeric'
                    pattern="[0-9]*"
                    onChange={e => setYear('20' + e?.target?.value)}
                  />
                </div>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={3} style={{paddingTop: '0.4rem'}}>
            <CustomInputLabel required={true}>
              State
            </CustomInputLabel>
            <select
              className='field'
              style={{...inputStyle, appearance: 'none'}}
              name='state'
              maxLength='2'
              placeholder='CA'
              autoComplete="billing address-level1"
              type="text"
              defaultValue={props?.user?.address_state || props?.user?.patient?.address_state || ''}
              onChange={e => setAddressState(e?.target?.value)}
            >
              {getStates().map(state => 
                <option key={state.value} value={state.value}>{state.label}</option>
              )}
            </select>
          </Grid>
          <Grid item xs={12} md={3} style={{paddingTop: '0.4rem'}}>
            <CustomInputLabel required={true}>
              Zip
            </CustomInputLabel>
            <input
              className='field'
              style={{...inputStyle}}
              name='zipcode'
              maxLength='5'
              placeholder='12345'
              autoComplete="billing postal-code"
              type="text"
              inputMode='numeric'
              pattern="[0-9]*"
              defaultValue={props?.user?.address_zip || props?.user?.patient?.address_zip || ''}
              onChange={e => setAddressZip(e?.target?.value)}
            />
          </Grid>
        </Grid>
        <Stack spacing={1} style={{marginTop: '1rem', marginBottom: '1rem'}}>
          {showOnboardingFields && 
            <Button
              sx={{
                width: 'fit-content',
                height: '30px',
                alignSelf: 'flex-start',
                gap: '8px',
                display: 'flex',
                border: '2px solid var(--button-stroke)',
                borderRadius: '8px',
                padding: '8px 7px',
                marginTop: '1rem',
              }}
              size='small'
              variant='primaryOutline'
              fullWidth={false}
              onClick={() => setPromoIsCollapsed(!promoIsCollapsed)}
            >
              <span className='PaymentForm__promo-button-icon'></span>
              <Typography variant='small' letterSpacing='0px'>
                Enter Promo Code
              </Typography>
            </Button>
          }
          <Collapse in={!promoIsCollapsed}>
            <Box className='PaymentForm__promo-code-container' sx={{ width: '100%' }}>
              <Stack>
                <CustomTextField
                  className='PaymentForm__promo-code-input'
                  label='Promo Code'
                  placeholder='ABC1234567'
                  defaultValue={props?.user?.promo_code || null}
                  variant='outlined'
                  width='100%'
                  onChange={e => setPromoCode(e?.target?.value)}
                />
              </Stack>
            </Box>
          </Collapse>
          {showOnboardingFields && 
            <Typography
              variant='large500Tight'
              fontStyle='italic'
              color='text.light'
              style={{paddingTop:'1rem'}}
            >
              By clicking continue, I recognize that MovementX will not submit payments to my insurance and will not accept
              reimbursement on my behalf.
            </Typography>
          }
          {!showOnboardingFields &&
            <Stack direction='row' spacing={0.5} alignItems='center' style={{paddingTop: '2em'}}>
              <IconShield color='primary' />
              <Typography variant='body' color='grey' style={{fontSize: '0.8rem'}}>
                MovementX does not store your credit card information. Payments are processed by a secure financial institution.
              </Typography>
            </Stack>
          }
          <Button
            variant='contained'
            onClick={onSubmit}
            disabled={payButtonDisabled}
          >
            {isSubmitting && <CircularProgress size={18} color='inherit' style={{marginRight: '0.5rem'}}/>}
            {isRetrying
              ? 'Retrying...'
              : (isSubmitting
                ? 'Processing payment...'
                : (showOnboardingFields ? 'Continue to Final Step' : 'Save')
              )
            }
          </Button>
        </Stack>
      </Box>
    );
};

const StaxPaymentFormContainer = props => {
  const {user} = props;
  if (!user) return <CircularProgress />;
  const isEditing = !!props?.user?.patient?.stax_customer_id;
  return <StaxPaymentForm {...props} isEditing={isEditing} />;
};

StaxPaymentFormContainer.propTypes = {
  user: PropTypes.object,
  auth: PropTypes.object,
  onSubmit: PropTypes.func,
  showOnboardingFields: PropTypes.bool,
};

export default StaxPaymentFormContainer;